/**
 * SecurityAuditPanel — Security Posture Dashboard (Tier 3)
 * 
 * Runs a live security audit and displays pass/warn/fail checks
 * grouped by category with a score gauge.
 * Integrates into CognitiveLab as a tab.
 */
import React, { useState, useEffect, useCallback } from 'react';

interface AuditCheck {
  category: string;
  check: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

interface AuditReport {
  timestamp: string;
  overall: 'pass' | 'warn' | 'fail';
  score: number;
  checks: AuditCheck[];
  summary: { pass: number; warn: number; fail: number; total: number };
}

interface Props {
  getToken: () => Promise<string>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const STATUS_CONFIG = {
  pass: { icon: '✅', color: '#22c55e', bg: 'bg-green-50 dark:bg-green-900/15', border: 'border-green-200 dark:border-green-800/40', text: 'text-green-700 dark:text-green-400' },
  warn: { icon: '⚠️', color: '#f59e0b', bg: 'bg-amber-50 dark:bg-amber-900/15', border: 'border-amber-200 dark:border-amber-800/40', text: 'text-amber-700 dark:text-amber-400' },
  fail: { icon: '❌', color: '#ef4444', bg: 'bg-red-50 dark:bg-red-900/15', border: 'border-red-200 dark:border-red-800/40', text: 'text-red-700 dark:text-red-400' },
};

export const SecurityAuditPanel: React.FC<Props> = ({ getToken }) => {
  const [report, setReport] = useState<AuditReport | null>(null);
  const [loading, setLoading] = useState(true);

  const runAudit = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/v1/health/audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.checks && Array.isArray(data.checks)) {
        setReport(data);
      } else if (data && data.error) {
        console.warn('Security audit returned error:', data.error);
      }
    } catch (e) {
      console.error('Security audit failed:', e);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { runAudit(); }, [runAudit]);

  // Group checks by category
  const grouped: Record<string, AuditCheck[]> = {};
  if (report && report.checks && Array.isArray(report.checks)) {
    for (const check of report.checks) {
      if (!grouped[check.category]) grouped[check.category] = [];
      grouped[check.category].push(check);
    }
  }

  const scoreColor = report
    ? report.score >= 80 ? '#22c55e' : report.score >= 60 ? '#f59e0b' : '#ef4444'
    : '#64748b';

  // SVG arc calculations for score gauge
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const progress = report ? (report.score / 100) * circumference : 0;

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-4xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Security Audit</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Comprehensive security posture analysis
              </p>
            </div>
          </div>
          <button
            onClick={runAudit}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold rounded-xl transition-colors"
          >
            {loading ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span>🔄</span>
            )}
            {loading ? 'Scanning...' : 'Re-scan'}
          </button>
        </div>

        {loading && !report ? (
          <div className="flex items-center justify-center py-20">
            <div className="space-y-3 text-center">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Running security audit...</p>
            </div>
          </div>
        ) : report && (
          <>
            {/* ── Score + Summary ── */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Gauge */}
              <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-5 flex flex-col items-center">
                <div className="relative w-40 h-40">
                  <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                    <circle cx="80" cy="80" r={radius} fill="none" stroke="currentColor"
                      className="text-slate-200 dark:text-slate-700" strokeWidth="8" />
                    <circle cx="80" cy="80" r={radius} fill="none" stroke={scoreColor}
                      strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={circumference} strokeDashoffset={circumference - progress}
                      style={{ transition: 'stroke-dashoffset 1.5s ease-out', filter: `drop-shadow(0 0 6px ${scoreColor}40)` }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-black" style={{ color: scoreColor }}>{report.score}</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">/ 100</span>
                  </div>
                </div>
              </div>

              {/* Summary cards */}
              {[
                { label: 'Passed', value: report.summary.pass, icon: '✅', color: '#22c55e' },
                { label: 'Warnings', value: report.summary.warn, icon: '⚠️', color: '#f59e0b' },
                { label: 'Failed', value: report.summary.fail, icon: '❌', color: '#ef4444' },
              ].map(s => (
                <div key={s.label} className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-5 flex flex-col items-center justify-center">
                  <span className="text-2xl mb-2">{s.icon}</span>
                  <span className="text-2xl font-black" style={{ color: s.color }}>{s.value}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{s.label}</span>
                </div>
              ))}
            </div>

            {/* ── Check Categories ── */}
            {Object.entries(grouped).map(([category, checks]) => {
              const catFails = checks.filter(c => c.status === 'fail').length;
              const catWarns = checks.filter(c => c.status === 'warn').length;
              const catIcon = catFails > 0 ? '❌' : catWarns > 0 ? '⚠️' : '✅';

              return (
                <div key={category} className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 dark:border-slate-700/50">
                    <span>{catIcon}</span>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">{category}</h3>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-auto">
                      {checks.length} check{checks.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-slate-700/30">
                    {checks.map((check, i) => {
                      const cfg = STATUS_CONFIG[check.status];
                      return (
                        <div key={i} className={`flex items-start gap-3 px-5 py-3 ${cfg.bg}`}>
                          <span className="text-sm mt-0.5">{cfg.icon}</span>
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium ${cfg.text}`}>{check.check}</span>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{check.detail}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* ── Timestamp ── */}
            <div className="text-center text-[10px] text-slate-400 dark:text-slate-500">
              Last scanned: {new Date(report.timestamp).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SecurityAuditPanel;
